import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { employeeAPI, fnfAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Loader2, Calculator, CheckCircle, AlertTriangle } from 'lucide-react';
import { formatIndianCurrency } from '../../lib/utils';
// axios removed

const FnFSettlement = ({ employeeId, open, onOpenChange, onSuccess }) => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [calculation, setCalculation] = useState(null);
    const [formData, setFormData] = useState({
        exit_date: new Date().toISOString().split('T')[0],
        reason: 'Resignation',
        remarks: ''
    });

    const handleCalculate = async () => {
        setLoading(true);
        try {
            const res = await fnfAPI.calculate(employeeId, {
                exit_date: formData.exit_date,
                reason: formData.reason
            });
            setCalculation(res.data);
            setStep(2);
        } catch (error) {
            toast.error(error.response?.data?.detail || "Calculation failed");
        } finally {
            setLoading(false);
        }
    };

    const handleFinalize = async () => {
        setLoading(true);
        try {
            await fnfAPI.finalize(employeeId, {
                ...calculation,
                ...formData,
                status: "APPROVED",
                company_id: "auto"
            });
            toast.success("Settlement Finalized & Employee Offboarded");
            onSuccess && onSuccess();
            setStep(3); // Go to success step
        } catch (error) {
            toast.error("Finalization failed");
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPdf = async () => {
        try {
            const res = await fnfAPI.downloadPdf(employeeId);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `FnF_Statement_${employeeId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("PDF Download Error:", error);
            // If response is blob, we might need to await text to see error
            if (error.response?.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    const json = JSON.parse(text);
                    toast.error(json.detail || "Failed to download PDF");
                } catch (e) {
                    toast.error("Failed to download PDF");
                }
            } else {
                toast.error(error.response?.data?.detail || "Failed to download PDF");
            }
        }
    };

    const reset = () => {
        setStep(1);
        setCalculation(null);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Full & Final Settlement</DialogTitle>
                    <DialogDescription>Initiate exit process and calculate dues.</DialogDescription>
                </DialogHeader>

                {step === 1 && (
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Exit Date</Label>
                                <Input
                                    type="date"
                                    value={formData.exit_date}
                                    onChange={e => setFormData({ ...formData, exit_date: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Reason</Label>
                                <Input
                                    value={formData.reason}
                                    onChange={e => setFormData({ ...formData, reason: e.target.value })}
                                    placeholder="e.g. Resignation, Termination"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Remarks</Label>
                            <Input
                                value={formData.remarks}
                                onChange={e => setFormData({ ...formData, remarks: e.target.value })}
                                placeholder="Optional remarks"
                            />
                        </div>

                        <div className="bg-yellow-50 p-3 rounded-md border border-yellow-200 flex items-start gap-2">
                            <AlertTriangle className="h-5 w-5 text-yellow-600 shrink-0" />
                            <p className="text-sm text-yellow-800">
                                This will deactivate the employee and their login access immediately upon finalization.
                            </p>
                        </div>
                    </div>
                )}

                {step === 2 && calculation && (
                    <div className="space-y-6 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <Card>
                                <CardHeader className="p-4 bg-muted/20">
                                    <CardTitle className="text-sm font-medium">Earnings</CardTitle>
                                </CardHeader>
                                <CardContent className="p-4 space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span>Pro-rata Salary</span>
                                        <span>{formatIndianCurrency(calculation.unpaid_salary)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span>Leave Encashment</span>
                                        <span>{formatIndianCurrency(calculation.leave_encashment)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span>Bonus Due</span>
                                        <span>{formatIndianCurrency(calculation.bonus_due)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span>Gratuity</span>
                                        <span>{formatIndianCurrency(calculation.gratuity)}</span>
                                    </div>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader className="p-4 bg-muted/20">
                                    <CardTitle className="text-sm font-medium">Deductions</CardTitle>
                                </CardHeader>
                                <CardContent className="p-4 space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span>Notice Recovery</span>
                                        <span>{formatIndianCurrency(calculation.notice_period_recovery || 0)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span>Asset Recovery</span>
                                        <span>{formatIndianCurrency(calculation.asset_recovery || 0)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span>Other</span>
                                        <span>{formatIndianCurrency(calculation.other_deductions || 0)}</span>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        <div className="flex justify-between items-center p-4 bg-primary/10 rounded-lg border border-primary/20">
                            <span className="font-bold text-lg">Net Payable</span>
                            <span className="font-bold text-2xl text-primary">{formatIndianCurrency(calculation.net_payable)}</span>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="flex flex-col items-center justify-center py-8 space-y-4">
                        <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                            <CheckCircle className="h-6 w-6 text-green-600" />
                        </div>
                        <div className="text-center">
                            <h3 className="text-lg font-semibold">Settlement Complete</h3>
                            <p className="text-sm text-muted-foreground">The employee has been successfully offboarded.</p>
                        </div>
                        <Button variant="outline" onClick={handleDownloadPdf} className="w-full sm:w-auto">
                            <Calculator className="mr-2 h-4 w-4" /> {/* Reuse calc icon or import Download */}
                            Download FnF Statement
                        </Button>
                    </div>
                )}

                <DialogFooter>
                    {step === 1 ? (
                        <Button onClick={handleCalculate} disabled={loading}>
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Calculate Dues
                        </Button>
                    ) : step === 2 ? (
                        <>
                            <Button variant="outline" onClick={() => setStep(1)} disabled={loading}>Back</Button>
                            <Button onClick={handleFinalize} disabled={loading} className="bg-green-600 hover:bg-green-700">
                                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Finalize & Offboard
                            </Button>
                        </>
                    ) : (
                        <Button onClick={() => onOpenChange(false)}>Close</Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default FnFSettlement;
